import { CustomsPackingProblemType, ProductInfo } from "@/model/HqPackingListModels";
import {
  CustomsPackingModel,
  HqPackingListState,
  PackingVolume,
  PackingWeight,
  SecondaryPackingModel,
} from "@/model/HenganLoadingPlanModels";
import { formatPrice } from "@/utils/number";

export type OverUnderageStatus =
  | "OVERDELIVERY"
  | "UNDERDELIVERY"
  | "ALL"
  | "NONE";

export function isOverUnderageBlocked(
  status?: OverUnderageStatus | string | null
): boolean {
  return (
    status === "OVERDELIVERY" ||
    status === "UNDERDELIVERY" ||
    status === "ALL"
  );
}

export function packingOverUnderageMarks(
  status?: OverUnderageStatus | string | null
): Array<"over" | "short"> {
  if (status === "OVERDELIVERY") {
    return ["over"];
  }
  if (status === "UNDERDELIVERY") {
    return ["short"];
  }
  if (status === "ALL") {
    return ["over", "short"];
  }
  return [];
}

export interface PackingRow {
  id?: string;
  packingId?: string;
  packingType?: string;
  selected?: boolean;
  loadingFirst?: boolean;
  unboxing?: boolean;
  displayCode?: string;
  packingCode?: string;
  descriptions?: string;
  declarationDescriptions?: string;
  volume?: string | number;
  netWeight?: string | number;
  grossWeight?: string | number;
  totalPrice?: string | number;
  totalProductQuantity?: string | number;
  purchaseOrderNumbers?: string;
  factoryName?: string;
  inventory?: string;
  useInventoryQuantity?: number;
  exception?: boolean;
  purchaseOrderExceptionsProcessed?: boolean;
  overUnderageStatus?: OverUnderageStatus | string;
  hqPackingListId?: string;
  hqPackingListCode?: string;
  deliveryNoteId?: string;
  deliveryNoteCode?: string;
}

export interface PackingListGroup {
  id: string;
  code: string;
  factoryName: string;
  deliveryNoteCode: string;
  items: PackingRow[];
}

/** 主包装编号按 `#` 拆分：先比前缀字符串，再比后缀数字 */
export function comparePackingCode(
  left?: string | null,
  right?: string | null
): number {
  const parse = (code?: string | null) => {
    const text = (code || "").trim();
    const index = text.indexOf("#");
    if (index < 0) {
      return { prefix: text, serial: Number.NaN };
    }
    return {
      prefix: text.slice(0, index),
      serial: Number(text.slice(index + 1)),
    };
  };
  const a = parse(left);
  const b = parse(right);
  if (a.prefix !== b.prefix) {
    return a.prefix.localeCompare(b.prefix, "zh-CN");
  }
  const aSerial = Number.isFinite(a.serial) ? a.serial : Number.POSITIVE_INFINITY;
  const bSerial = Number.isFinite(b.serial) ? b.serial : Number.POSITIVE_INFINITY;
  if (aSerial !== bSerial) {
    return aSerial - bSerial;
  }
  return (left || "").localeCompare(right || "", "zh-CN");
}

export function sortPackingRowsByCode<T extends PackingRow>(rows: T[]): T[] {
  return [...rows].sort((a, b) =>
    comparePackingCode(
      a.packingCode || a.displayCode,
      b.packingCode || b.displayCode
    )
  );
}

export class CreateHenganLoadingPlanRequest {
  customerId: string;
  companyCode: string;
  shippingOrderId: string;
  transshipDelivery: boolean;
  transshipDeclaration: boolean;
  logisticsSpecialistId: string;
  remark: string;
  referencePackingData: boolean;
  packingInfos: PackingInfo[];
}

export class HenganLoadingPlanPackingInfo {
  henganLoadingPlanId: string;
  henganLoadingPlanCode: string;
  logisticsSpecialistId: string;
  logisticsSpecialistName: string;
  customerId: string;
  customerAbbreviation: string;
  transshipDelivery: boolean;
  transshipDeclaration: boolean;
  remark: string;
  packingInfos: PackingInfo[];
}

export class PackingInfo {
  id: string;
  henganLoadingPlanId: string;
  packingId: string;
  packingType: string;
  quantity: number;
  loadingFirst: boolean;
  packingCode: string;
  declarationDescriptions: string;
  descriptions?: string;
  volume: number | string;
  purchaseOrderNumbers: string;
  factoryName: string;
  totalProductQuantity: number | string;
  totalPrice: string;
  hqPackingListCode: string;
  deliveryNoteCode: string;
  deliveryNoteId: string;
  hqPackingListId: string;
  unboxing: boolean;
  selected?: boolean;
  netWeight?: string | number | null;
  grossWeight?: string | number | null;
  inventory?: string;
  useInventoryQuantity?: number;
  purchaseOrderExceptionsProcessed?: boolean;
  overUnderageStatus?: OverUnderageStatus | string;
  exception?: boolean;
  hqPackingListState?: HqPackingListState;

  constructor(
    packingId: string,
    packingType: string,
    quantity: number,
    loadingFirst: boolean,
    packingCode: string,
    declarationDescriptions: string,
    volume: string,
    purchaseOrderNumbers: string,
    factoryName: string,
    totalProductQuantity: number,
    totalPrice: string,
    hqPackingListCode: string,
    deliveryNoteCode: string,
    hqPackingListId: string,
    deliveryNoteId: string,
    unboxing: boolean
  ) {
    this.packingId = packingId;
    this.packingType = packingType;
    this.quantity = quantity;
    this.loadingFirst = loadingFirst;
    this.packingCode = packingCode;
    this.declarationDescriptions = declarationDescriptions;
    this.volume = parseFloat(volume);
    this.purchaseOrderNumbers = purchaseOrderNumbers;
    this.factoryName = factoryName;
    this.totalProductQuantity = totalProductQuantity;
    this.totalPrice = totalPrice;
    this.hqPackingListCode = hqPackingListCode;
    this.deliveryNoteCode = deliveryNoteCode;
    this.hqPackingListId = hqPackingListId;
    this.deliveryNoteId = deliveryNoteId;
    this.unboxing = unboxing;
    this.selected = false;
  }
}

export class Customer {
  id: string;
  code: string;
  abbreviation: string;
  logisticsSpecialistId: string;
  logisticsSpecialistName: string;
}

export class CascaderOptionItem {
  id: string;
  code: string;
  bePackaged: boolean;
  disabled: boolean;
  factoryAbbreviation: string;
  children: CascaderOptionItem[];
}

export class CustomsPackingTableRowData {
  id: string;
  deliveryNoteId: string;
  hqPackingListId: string;
  deliveryNoteCode: string;
  hqPackingListCode: string;
  hqPackingListState: HqPackingListState;
  factoryName: string;
  displayCode: string;
  volume: string;
  grossWeight: string;
  netWeight: string;
  selected: boolean;
  loadingFirst: boolean;
  indeterminate: boolean;
  totalPrice: string;
  descriptions: string;
  declarationDescriptions: string;
  purchaseOrderNumbers: string;
  totalProductQuantity: number;
  inventory: string;
  useInventoryQuantity: number;
  purchaseOrderExceptionsProcessed: boolean;
  overUnderageStatus: OverUnderageStatus | string;
  exception: boolean;
  exceptionReason: CustomsPackingProblemType;
  secondaryRows: SecondaryPackingTableRowData[] = [];

  constructor(
    deliveryNoteId: string,
    hqPackingListId: string,
    deliveryNoteCode: string,
    hqPackingListCode: string,
    hqPackingListState: HqPackingListState,
    factoryName: string,
    customsPacking: CustomsPackingModel
  ) {
    this.deliveryNoteId = deliveryNoteId;
    this.hqPackingListId = hqPackingListId;
    this.deliveryNoteCode = deliveryNoteCode;
    this.hqPackingListCode = hqPackingListCode;
    this.hqPackingListState = hqPackingListState;
    this.factoryName = factoryName;
    if (customsPacking) {
      this.id = customsPacking.id;
      this.volume = this.calculateVolume(customsPacking.packingVolume);
      this.grossWeight = String(customsPacking.packingWeight?.grossWeight);
      this.netWeight = String(customsPacking.packingWeight?.netWeight);
      this.inventory = customsPacking.inventory;
      this.purchaseOrderExceptionsProcessed =
        customsPacking.purchaseOrderExceptionsProcessed;
      this.overUnderageStatus = customsPacking.overUnderageStatus || "NONE";
      this.exception = customsPacking.exception;
      this.exceptionReason = customsPacking.exceptionReason;
      this.useInventoryQuantity = customsPacking.useInventoryQuantity;
      this.displayCode =
        customsPacking.code.typeCode +
        customsPacking.code.selfCode +
        "#" +
        customsPacking.code.serialNumber;
      this.secondaryRows = customsPacking.secondaryPackingSet?.map(
        (value) =>
          new SecondaryPackingTableRowData(
            customsPacking.id,
            factoryName,
            value,
            hqPackingListState,
            this.inventory
          )
      );
      const totalPrice = this.secondaryRows
        ?.map((value) => value.totalPrice)
        .reduce((pre, cur) => pre + Number(cur), 0);
      this.totalPrice = formatPrice(totalPrice);
      this.descriptions = this.unique(
        this.secondaryRows?.map((value) => value.descriptions)
      ).join(",");
      this.declarationDescriptions = this.unique(
        this.secondaryRows?.map((value) => value.declarationDescriptions)
      ).join(",");
      this.purchaseOrderNumbers = this.unique(
        this.secondaryRows?.map((value) => value.purchaseOrderNumbers)
      ).join(",");
      this.totalProductQuantity = this.secondaryRows
        ?.map((value) => value.totalProductQuantity)
        .reduce((pre, cur) => pre + Number(cur), 0);
    }
    this.selected = false;
    this.loadingFirst = false;
    this.indeterminate = false;
  }

  unique(arr) {
    return Array.from(new Set(arr));
  }

  calculateVolume(packingVolume: PackingVolume): string {
    return new PackingVolume(
      packingVolume.length,
      packingVolume.width,
      packingVolume.height
    ).calculateVolume();
  }

  /**
   * 编辑场景下，已占用主包装不会出现在「加载包装」结果中。
   * 从前端已添加列表回灌一条未添加行，便于移除后再次勾选。
   */
  static fromPackingInfo(info: PackingInfo): CustomsPackingTableRowData {
    const row = new CustomsPackingTableRowData(
      info.deliveryNoteId,
      info.hqPackingListId,
      info.deliveryNoteCode,
      info.hqPackingListCode,
      info.hqPackingListState,
      info.factoryName,
      null
    );
    row.id = info.packingId;
    row.displayCode = info.packingCode;
    row.declarationDescriptions = info.declarationDescriptions || "";
    row.descriptions = info.descriptions || info.declarationDescriptions || "";
    row.volume = info.volume != null ? String(info.volume) : "0";
    row.netWeight =
      info.netWeight != null && info.netWeight !== ""
        ? String(info.netWeight)
        : "--";
    row.grossWeight =
      info.grossWeight != null && info.grossWeight !== ""
        ? String(info.grossWeight)
        : "--";
    row.totalPrice = info.totalPrice != null ? String(info.totalPrice) : "0";
    row.totalProductQuantity = Number(info.totalProductQuantity || 0);
    row.purchaseOrderNumbers = info.purchaseOrderNumbers || "";
    row.inventory = info.inventory || "NO";
    row.useInventoryQuantity = Number(info.useInventoryQuantity || 0);
    row.purchaseOrderExceptionsProcessed =
      info.purchaseOrderExceptionsProcessed !== false;
    // 已添加接口不含超欠发，回灌后按 NONE 处理
    row.overUnderageStatus = info.overUnderageStatus || "NONE";
    row.exception = !!info.exception;
    row.loadingFirst = !!info.loadingFirst;
    row.selected = false;
    row.indeterminate = false;
    row.secondaryRows = [];
    return row;
  }
}

class SecondaryPackingTableRowData extends CustomsPackingTableRowData {
  pid: string;
  cartonNumber: number;
  hqPackingListState: HqPackingListState;
  productRows: ProductTableRowData[] = [];
  inventory: string;

  constructor(
    pid: string,
    factoryName: string,
    secondaryPacking: SecondaryPackingModel,
    hqPackingListState: HqPackingListState,
    inventory: string
  ) {
    super(null, null, null, null, null, factoryName, null);
    this.id = secondaryPacking.id;
    this.pid = pid;
    this.cartonNumber = secondaryPacking.cartonNumber;
    this.factoryName = factoryName;
    this.hqPackingListState = hqPackingListState;
    const packingWeights: PackingWeight[] = secondaryPacking.packingInfos
      .map((value) => value.packingWeight)
      .reduce((pre, cur) => pre.concat(cur), []);
    this.netWeight = packingWeights
      .map((value) => value.netWeight)
      .reduce((pre, cur) => pre + Number(cur), 0)
      .toFixed(2);
    this.grossWeight = packingWeights
      .map((value) => value.grossWeight)
      .reduce((pre, cur) => pre + Number(cur), 0)
      .toFixed(2);
    this.volume = secondaryPacking.packingInfos
      .map((value) => this.calculateVolume(value.packingVolume))
      .reduce((pre, cur) => pre + Number(cur), 0)
      .toFixed(2);
    const productInfos = secondaryPacking.packingInfos
      .map((value) => value.productInfos)
      .reduce((pre, cur) => pre.concat(cur), []);
    this.totalPrice = productInfos
      .map((value) => value.unitPrice * value.totalNumberPcs)
      .reduce((pre, cur) => pre + Number(cur), 0)
      .toFixed(2);
    this.descriptions = super
      .unique(productInfos.map((value) => value.description))
      .join(",");
    this.declarationDescriptions = super
      .unique(productInfos.map((value) => value.declarationDescription))
      .join(",");
    this.purchaseOrderNumbers = super
      .unique(productInfos.map((value) => value.purchaseOrderNumber))
      .join(",");
    this.totalProductQuantity = productInfos
      .map((value) => value.totalNumberPcs)
      .reduce((pre, cur) => pre + Number(cur), 0);
    this.inventory = inventory;
    this.productRows = productInfos.map((value) => {
      return {
        pid: secondaryPacking.id,
        quantity: value.totalNumberPcs,
        selected: false,
        loadingFirst: false,
        hqPackingListState: hqPackingListState,
        ...value,
      };
    });
  }
}

class ProductTableRowData extends ProductInfo {
  pid: string;
  quantity = 0;
  selected = false;
  loadingFirst = false;
}
